/**
 * Hook for real-time usage updates via WebSocket
 */

import { useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/subscription/SubscriptionContext';
import socketService, { UsageUpdateEvent } from '../services/SocketService';

export const useRealtimeUsage = () => {
  const { user, isAuthenticated } = useAuth();
  const { refreshSubscription } = useSubscription();
  const subscriberIdRef = useRef<string | null>(null);

  // Handle usage update events
  const handleUsageUpdate = useCallback(
    (data: UsageUpdateEvent) => {
      // Only process updates for the current user
      if (!user || data.user_id !== user.id) {
        return;
      }

      // Refresh subscription data to get latest usage
      // This ensures we get all usage fields updated consistently
      refreshSubscription();

      // TODO: For more granular updates, we could update specific fields
      // instead of refreshing everything
    },
    [user, refreshSubscription],
  );

  // When the user becomes available, ensure the socket joins the user room
  useEffect(() => {
    if (user?.id) {
      socketService.updateAuth(user.id);
    }
  }, [user?.id]);

  // Set up WebSocket event listeners
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const subscriberId = socketService.subscribeToEvents({
      onUsageUpdated: handleUsageUpdate,
    });
    subscriberIdRef.current = subscriberId;

    // Connect to WebSocket if not already connected
    const connectSocket = async () => {
      try {
        await socketService.connect();
      } catch (error) {
        console.error('📊 useRealtimeUsage: Failed to connect to WebSocket:', error);
      }
    };

    connectSocket();

    // Cleanup function to unsubscribe this specific subscriber
    return () => {
      if (subscriberIdRef.current) {
        socketService.unsubscribeSubscriber(subscriberIdRef.current);
        subscriberIdRef.current = null;
      }
    };
  }, [isAuthenticated, handleUsageUpdate]);

  return {
    // Could return additional utilities here if needed
    refreshUsage: refreshSubscription,
  };
};
